// src/pages/dashboard/AdminProfile.jsx
import React, { useEffect, useMemo, useState } from "react";
import PageShell from "./PageShell";
import { profileApi } from "../../core/api/profileApi";

const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

export default function AdminProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [user, setUser] = useState(null);

  // Edit form
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState(""); // optional (backend may ignore)
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  // Password form (optional endpoint)
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState("");

  const fullName = useMemo(() => {
    const parts = [firstName, middleName, lastName].map((x) => safeStr(x).trim()).filter(Boolean);
    return parts.join(" ");
  }, [firstName, middleName, lastName]);

  const loadProfile = async () => {
    try {
      setError("");
      setLoading(true);
      const res = await profileApi.getMe();
      const data = res?.data || null;
      setUser(data);

      setFirstName(safeStr(data?.first_name));
      setMiddleName(safeStr(data?.middle_name)); // if exists
      setLastName(safeStr(data?.last_name));
      setPhone(safeStr(data?.phone_number || data?.phone));
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "Failed to load profile.";
      setError(msg);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    try {
      setSaving(true);
      setError("");

      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: phone.trim(),
      };

      // send only if you want (backend may ignore if not present)
      if (middleName.trim()) payload.middle_name = middleName.trim();

      const res = await profileApi.updateMe(payload);
      const updated = res?.data || null;

      // Some APIs return updated object; if not, reload.
      if (updated) {
        setUser(updated);
        setFirstName(safeStr(updated?.first_name));
        setMiddleName(safeStr(updated?.middle_name));
        setLastName(safeStr(updated?.last_name));
        setPhone(safeStr(updated?.phone_number || updated?.phone));
      } else {
        await loadProfile();
      }
    } catch (e) {
      const data = e?.response?.data;
      const msg =
        data?.detail ||
        data?.message ||
        (typeof data === "object" ? JSON.stringify(data) : "") ||
        "Failed to update profile.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const onChangePassword = async () => {
    setPwMsg("");
    if (!pwOld.trim() || !pwNew.trim()) {
      setPwMsg("Old password and new password are required.");
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwMsg("New password and confirm password do not match.");
      return;
    }

    try {
      setPwSaving(true);
      setPwMsg("");

      await profileApi.changePassword({
        old_password: pwOld,
        new_password: pwNew,
      });

      setPwOld("");
      setPwNew("");
      setPwConfirm("");
      setPwMsg("✅ Password updated successfully.");
    } catch (e) {
      const data = e?.response?.data;
      const msg =
        data?.detail ||
        data?.message ||
        (typeof data === "object" ? JSON.stringify(data) : "") ||
        "Password change failed. (Check endpoint URL / backend support)";
      setPwMsg(msg);
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <PageShell title="Profile">
      {/* Top status */}
      {loading ? (
        <div className="text-secondary/70">Loading profile...</div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {!loading && user ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Profile summary */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-secondary/20 bg-white/70 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-secondary text-accent grid place-items-center font-bold">
                  {safeStr(user?.username || user?.email || "U")
                    .slice(0, 1)
                    .toUpperCase()}
                </div>
                <div>
                  <div className="text-secondary font-semibold leading-tight">
                    {safeStr(user?.first_name || "") || safeStr(user?.username || "") || "User"}
                    {user?.last_name ? ` ${safeStr(user?.last_name)}` : ""}
                  </div>
                  <div className="text-xs text-secondary/70">
                    {safeStr(user?.email)}
                  </div>
                </div>
              </div>

              <div className="mt-5 space-y-3 text-sm">
                <Row label="Username" value={user?.username} />
                <Row label="Email" value={user?.email} />
                <Row label="Role" value={user?.role ? String(user.role).toUpperCase() : "-"} />
                <Row label="Is Staff" value={typeof user?.is_staff === "boolean" ? (user.is_staff ? "YES" : "NO") : "-"} />
                <Row label="Phone" value={user?.phone_number || user?.phone || "-"} />
                <Row label="Created At" value={user?.created_at || "-"} />
              </div>

              <div className="mt-5">
                <button
                  onClick={loadProfile}
                  className="w-full rounded-xl border border-secondary/30 px-4 py-2 text-sm text-secondary hover:bg-primary/30 transition"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT: Edit profile + password */}
          <div className="lg:col-span-2 space-y-6">
            {/* Edit profile card */}
            <div className="rounded-2xl border border-secondary/20 bg-white/70 p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-secondary font-semibold">Edit Profile</div>
                  <div className="text-xs text-secondary/70">
                    Update your name and phone number
                  </div>
                </div>

                <button
                  onClick={onSave}
                  disabled={saving}
                  className={[
                    "rounded-xl px-4 py-2 text-sm font-medium transition",
                    "bg-secondary text-accent hover:opacity-90",
                    saving ? "opacity-60 cursor-not-allowed" : "",
                  ].join(" ")}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <Field
                  label="Middle Name (optional)"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                />
                <Field
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
                <Field
                  label="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 9876543210"
                />
                <div className="sm:col-span-2 rounded-xl border border-secondary/20 bg-white/60 p-4">
                  <div className="text-xs text-secondary/70">Full Name Preview</div>
                  <div className="text-secondary font-semibold">
                    {fullName || "-"}
                  </div>
                </div>
              </div>
            </div>

            {/* Change password card (optional endpoint) */}
            <div className="rounded-2xl border border-secondary/20 bg-white/70 p-5 shadow-sm">
              <div className="text-secondary font-semibold">Change Password</div>
              <div className="text-xs text-secondary/70">
                This will work only if backend supports <code className="px-1">/api/users/change-password/</code>
              </div>

              {pwMsg ? (
                <div className="mt-4 rounded-xl border border-secondary/20 bg-white/70 px-4 py-3 text-sm text-secondary">
                  {pwMsg}
                </div>
              ) : null}

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field
                  label="Old Password"
                  type="password"
                  value={pwOld}
                  onChange={(e) => setPwOld(e.target.value)}
                />
                <Field
                  label="New Password"
                  type="password"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                />
                <Field
                  label="Confirm Password"
                  type="password"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                />
              </div>

              <div className="mt-4">
                <button
                  onClick={onChangePassword}
                  disabled={pwSaving}
                  className={[
                    "rounded-xl px-4 py-2 text-sm font-medium transition",
                    "border border-secondary/30 text-secondary hover:bg-primary/30",
                    pwSaving ? "opacity-60 cursor-not-allowed" : "",
                  ].join(" ")}
                >
                  {pwSaving ? "Updating..." : "Update Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="text-secondary/70">{label}</div>
      <div className="text-secondary font-medium text-right break-all">
        {value === null || value === undefined || value === "" ? "-" : String(value)}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <div className="text-xs text-secondary/70 mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-secondary/30 bg-white/70 px-3 py-2 text-sm text-secondary outline-none focus:ring-2 focus:ring-secondary/30"
      />
    </label>
  );
}
